import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Compact uploader for the host's portrait / property-branded avatar.
 * Used on /success — shown publicly on /guest/:id when the application is approved.
 */
export default function HostAvatarUploader({ submissionId }) {
  const [avatar, setAvatar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    if (!submissionId) return;
    try {
      const res = await publicApi.get(`/submissions/${submissionId}/files`);
      const existing = (res.data || []).find((f) => f.category === "host_avatar");
      setAvatar(existing || null);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose a photo for your avatar.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "host_avatar");
      await publicApi.post(`/submissions/${submissionId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar updated.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (!submissionId) return null;

  return (
    <div
      data-testid="host-avatar-uploader"
      className="border border-[#E3DED5] bg-white p-6 flex items-center gap-5"
    >
      <label
        data-testid="host-avatar-dropzone"
        className={cn(
          "cursor-pointer w-24 h-24 shrink-0 relative overflow-hidden",
          avatar ? "border border-[#C03028]" : "border border-dashed border-[#E3DED5] hover:border-[#C03028]"
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={uploading}
          onChange={onUpload}
          data-testid="host-avatar-input"
          className="hidden"
        />
        {avatar ? (
          <>
            <img
              src={`${API}/files/${avatar.id}/download`}
              alt="Host avatar"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute top-1 right-1 bg-[#C03028] text-white w-5 h-5 flex items-center justify-center">
              <Check size={11} strokeWidth={3} />
            </span>
          </>
        ) : uploading ? (
          <div className="w-full h-full bg-[#F2EFE9] flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#C03028]" />
          </div>
        ) : (
          <div className="w-full h-full bg-[#F2EFE9] flex flex-col items-center justify-center text-[#8E8F90]">
            <Upload size={18} />
            <span className="text-[9px] uppercase tracking-[0.2em] mt-1">Add</span>
          </div>
        )}
      </label>

      <div className="flex-1">
        <div className="eyebrow">Your host avatar</div>
        <h3 className="font-serif-display text-xl text-[#2A1F1A] mt-1">
          A face to greet your guests
        </h3>
        <p className="text-xs text-[#6E6E70] mt-1.5 leading-relaxed">
          {avatar
            ? "Looking great. Your avatar will appear on your guest welcome page once your application is approved."
            : "Upload a portrait or a property-branded mark. It will appear on your guest welcome page."}
        </p>
      </div>
    </div>
  );
}
